import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';

import { type DemoOutboxMessage, DemoOutboxMessageSchema } from '@arya/shared/dev-accounts';

import { Type } from 'typebox';
import { Value } from 'typebox/value';

export interface DemoOutboxEntry extends DemoOutboxMessage {
  readonly receivedAt: string;
}

const DemoOutboxEntries = Type.Array(Type.Object({ ...DemoOutboxMessageSchema.properties, receivedAt: Type.String() }));

const STORAGE_KEY = 'noxo-demo-outbox';
const REAL_EMAIL_KEY = 'noxo-demo-real-email';
const KEPT_MESSAGES = 12;

@Injectable({ providedIn: 'root' })
export class DemoOutbox {
  private readonly storage = inject(DOCUMENT).defaultView?.localStorage;
  private readonly entries = signal<readonly DemoOutboxEntry[]>(this.restore());
  private readonly realEmailOn = signal(this.read(REAL_EMAIL_KEY) === '1');

  private readonly seenAt = signal(new Date().toISOString());

  public readonly messages = this.entries.asReadonly();
  public readonly unseenCode = computed(() => {
    const [latest] = this.entries();
    return latest !== undefined && latest.receivedAt > this.seenAt() ? latest.code : undefined;
  });
  public readonly realEmail = this.realEmailOn.asReadonly();

  public setRealEmail(on: boolean): void {
    this.realEmailOn.set(on);
    this.write(REAL_EMAIL_KEY, on ? '1' : '0');
  }

  public record(messages: readonly DemoOutboxMessage[], receivedAt: Date): void {
    if (messages.length === 0) return;
    const arrived = messages.map((message) => ({ ...message, receivedAt: receivedAt.toISOString() })).toReversed();
    this.entries.update((kept) => [...arrived, ...kept].slice(0, KEPT_MESSAGES));
    this.persist();
  }

  public latestCode(channel: DemoOutboxMessage['channel'], to: string): string | undefined {
    const recipient = to.trim().toLowerCase();
    return this.entries().find((entry) => entry.channel === channel && entry.to.toLowerCase() === recipient)?.code;
  }

  public markSeen(): void {
    this.seenAt.set(new Date().toISOString());
  }

  public clear(): void {
    this.entries.set([]);
    this.persist();
  }

  private restore(): readonly DemoOutboxEntry[] {
    try {
      const entries: unknown = JSON.parse(this.read(STORAGE_KEY) ?? '[]');
      return Value.Check(DemoOutboxEntries, entries) ? entries : [];
    } catch {
      return [];
    }
  }

  private persist(): void {
    this.write(STORAGE_KEY, JSON.stringify(this.entries()));
  }

  private read(key: string): string | undefined {
    try {
      return this.storage?.getItem(key) ?? undefined;
    } catch {
      return undefined;
    }
  }

  private write(key: string, value: string): void {
    try {
      this.storage?.setItem(key, value);
    } catch {
      return;
    }
  }
}
